import React, {
  useRef, useState, useEffect,
} from 'react';
import propTypes from 'prop-types';
// import { bool, array, oneOf } from 'prop-types';
import ImageViewer from '@prism/imageviewer';
import { FilmstripWithCarousel, ImageGridWithCarousel } from '@prism/filmstrip';
import classNames from 'classnames';
import {
  handleExitFullScreenClick,
  handleFullScreenClick,
  handleZoomInClick,
  handleZoomOutClick,
  handleFyuseIdsAnd,
  getCategoryCounts,
  normalizeAsset,
} from './utils';
import { MediaAssetsType } from '../../types/index';
import Filters from './Filters';
import FullScreen from './FullScreen';
import { MultimediaViewerProvider } from './MultimediaViewerCtx';
import { IMAGE_VIEWER_CONTROLS_HIDDEN_CLASS } from '../../types/constants';

const MultimediaViewer = ({
  mediaAssets,
  disableFilters,
  hideAddOns,
  hideFilters,
  showGrid,
  customButtons,
  showFilters,
  showFullScreenControls,
}) => {
  const [unfilteredAssets, setUnfilteredAssets] = useState(
    mediaAssets.map(normalizeAsset)
  );
  const [filteredAssets, setFilteredAssets] = useState(unfilteredAssets);
  const [activeFilter, setActiveFilter] = useState('ALL');

  useEffect(() => {
    handleFyuseIdsAnd(setUnfilteredAssets, mediaAssets, setFilteredAssets);
  }, [mediaAssets]);

  const [fullScreenHidden, setFullScreenHidden] = useState(true);
  const multiMediaViewerRef = useRef();

  const constructedFilters = (props) => <Filters {...props} />;

  const filterMediaAssets = (category) => {
    const originalAssets = unfilteredAssets.length === 0 ? mediaAssets : unfilteredAssets;
    return category === 'ALL'
      ? setFilteredAssets(originalAssets)
      : setFilteredAssets(
        unfilteredAssets.filter((asset) => asset.category === category)
      );
  };

  const filtersCommonProps = {
    filterMediaAssets,
    disabled: disableFilters,
    hidden: hideFilters,
    activeFilter,
    showFilters,
    setActiveFilter,
    categoryCounts: getCategoryCounts(unfilteredAssets),
  };

  const addOnClassNames = classNames({
    'multimediaviewer-add-ons': true,
    'hide-add-ons': hideAddOns,
  });

  const fullScreenClassNames = classNames({
    'imageviewer-fullscreen': true,
    [`${IMAGE_VIEWER_CONTROLS_HIDDEN_CLASS}`]: fullScreenHidden,
  });

  const multiMediaClassNames = classNames({
    multimediaviewer: true,
    'left-mounted-filters': showFilters === 'left',
  });

  const multiMediaContainerClassNames = classNames({
    'multimediaviewer-main-container': true,
    'multimediaviewer-width-66': showFilters === 'left',
  });

  const wrapperClassNames = classNames({
    'multimediaviewer-wrapper': true,
    fullscreen: !fullScreenHidden,
  });

  return (
    <div ref={multiMediaViewerRef} className={wrapperClassNames}>
      <MultimediaViewerProvider
        carouselType={showGrid ? 'image-grid' : 'filmstrip'}
      >
        <div className={multiMediaClassNames}>
          {showFilters === 'left' && (
            <div className="left-mounted-filters-container">
              {constructedFilters({ ...filtersCommonProps, showFilters })}
            </div>
          )}
          <div className={multiMediaContainerClassNames}>
            <ImageViewer
              mediaAssets={filteredAssets}
              handleFullScreenClick={handleFullScreenClick(
                multiMediaViewerRef,
                setFullScreenHidden
              )}
              handleExitFullScreenClick={handleExitFullScreenClick(
                multiMediaViewerRef,
                setFullScreenHidden
              )}
              handleZoomInClick={() => {
                handleZoomInClick(multiMediaViewerRef);
              }}
              handleZoomOutClick={() => {
                handleZoomOutClick(multiMediaViewerRef);
              }}
              fullScreenHidden={fullScreenHidden}
              customButtons={customButtons}
              showFullScreenControls={showFullScreenControls}
            />
            <div className={addOnClassNames}>
              {showGrid ? (
                <ImageGridWithCarousel mediaAssets={filteredAssets} />
              ) : (
                <FilmstripWithCarousel mediaAssets={filteredAssets} />
              )}
              {showFilters && constructedFilters({ ...filtersCommonProps })}
            </div>
            <FullScreen className={fullScreenClassNames} />
          </div>
        </div>
      </MultimediaViewerProvider>
    </div>
  );
};

const defaultProps = {
  mediaAssets: [],
  disableFilters: false,
  hideAddOns: false,
  hideFilters: false,
  showGrid: false,
  customButtons: [],
  showFilters: true,
  showFullScreenControls: true,
};

MultimediaViewer.propTypes = {
  mediaAssets: MediaAssetsType,
  disableFilters: propTypes.bool,
  hideAddOns: propTypes.bool,
  hideFilters: propTypes.bool,
  showGrid: propTypes.bool,
  customButtons: propTypes.array,
  showFilters: propTypes.oneOfType([propTypes.string, propTypes.bool]),
  showFullScreenControls: propTypes.bool,
};

MultimediaViewer.defaultProps = defaultProps;

export default MultimediaViewer;
